// src/services/AuthService.ts
import { UserManager, User, UserManagerSettings } from 'oidc-client-ts';
import oidcConfig from './oidc';

class AuthService {
  private userManager: UserManager;

  constructor() {
    this.userManager = new UserManager(oidcConfig as UserManagerSettings);
  }

  // 用户登录
  login = async (): Promise<void> => {
    try {
      await this.userManager.signinRedirect({
        state: window.location.pathname, // 保存当前路径，登录后跳回
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // 处理回调
  handleCallback = async (): Promise<User | undefined> => {
    try {
      // 使用 signinRedirectCallback 处理重定向回调
      // 这会从当前 URL 中提取 state 和 code 参数
      const user = await this.userManager.signinRedirectCallback();
      return user;
    } catch (error: any) {
      // 先检查是否已经有有效的 user（可能 signinRedirectCallback 已经成功保存了 user）
      // 即使抛出错误，user 也可能已经被保存
      const existingUser = await this.userManager.getUser();
      if (existingUser && existingUser.access_token) {
        // 检查是否是 "No matching state" 错误（这是预期的，因为 state 已经被处理）
        const isNoMatchingState = error?.message?.includes('No matching state found in storage');
        if (isNoMatchingState) {
          // 这是正常情况：signinRedirectCallback 已经处理了回调并保存了 user
          // state 被清除是正常的，不需要报错
          console.log('signinRedirectCallback 处理完成，已获取到有效的 user');
          return existingUser;
        }
        
        // 检查是否是 invalid_grant 错误（可能是自动刷新导致的）
        const isInvalidGrant = error?.error === 'invalid_grant' || 
                              error?.message?.includes('invalid_grant');
        if (isInvalidGrant) {
          console.warn('检测到 invalid_grant 错误（可能是自动刷新导致），但已存在有效的 user，使用已保存的 user');
          return existingUser;
        }
      }
      
      // 如果没有找到已保存的 user，才尝试 signinCallback
      // 但先检查错误类型，避免不必要的尝试
      const isNoMatchingState = error?.message?.includes('No matching state found in storage');
      if (isNoMatchingState) {
        // 如果是因为 state 不匹配，且没有已保存的 user，说明确实有问题
        console.warn('signinRedirectCallback 失败：No matching state，且未找到已保存的 user');
        throw error;
      }
      
      console.warn('signinRedirectCallback 失败，尝试 signinCallback:', error.message || error);
      // 如果失败，尝试使用 signinCallback（适用于某些场景）
      try {
        return await this.userManager.signinCallback();
      } catch (callbackError: any) {
        // 再次检查是否有已保存的 user
        const existingUser = await this.userManager.getUser();
        if (existingUser && existingUser.access_token) {
          console.warn('虽然回调处理出错，但检测到已保存的有效 user，使用已保存的 user');
          return existingUser;
        }
        
        // 如果 signinCallback 也是 No matching state 错误，且没有已保存的 user，才报错
        const isNoMatchingState = callbackError?.message?.includes('No matching state found in storage');
        if (isNoMatchingState) {
          console.warn('signinCallback 也失败：No matching state，且未找到已保存的 user');
        } else {
          console.error('signinCallback 失败:', callbackError);
        }
        
        throw callbackError;
      }
    }
  };

  // 获取当前用户
  getUser = async (): Promise<User | undefined> => {
    const user = await this.userManager.getUser();
    return user || undefined;
  };

  // 获取Token
  getToken = async (): Promise<string | undefined> => {
    const user = await this.getUser();
    return user?.access_token || undefined;
  };

  // 获取用户信息
  getUserInfo = async (): Promise<any> => {
    const user = await this.getUser();
    return user?.profile || undefined;
  };

  // 登出
  logout = async (): Promise<void> => {
    try {
      await this.userManager.signoutRedirect();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // 监听用户状态变化
  onUserLoaded = (callback: (user: User) => void): void => {
    this.userManager.events.addUserLoaded(callback);
  };

  // 监听Token过期
  onAccessTokenExpired = (callback: () => void): void => {
    this.userManager.events.addAccessTokenExpired(callback);
  };

  // 移除监听器
  removeUserLoadedListener = (callback: (user: User) => void): void => {
    this.userManager.events.removeUserLoaded(callback);
  };
}

export default new AuthService();